import { type FC, useEffect, useMemo, useState } from 'react';
import { SidePanel } from '@carbon/ibm-products';
import {
  Dropdown,
  InlineNotification,
  RadioButton,
  RadioButtonGroup,
} from '@carbon/react';
import { DestroyConfirmation } from 'components/destroy-confirmation/destroy-confirmation';
import { formatNumber } from 'utils/number';
import { polygonAreaAcres } from 'utils/geometry-measure';
import type { ReferenceLocationResponse } from 'types/api-responses';
import type { GeometryUpload } from 'types/area-geometry';
import 'components/inline-edit/ibm-products-config';
import type { MapWorkspaceState } from 'components/query-map/map-workspace-state';
import { CreateAreaDetails } from './create-area-details';
import {
  buildAreaMetadata,
  buildAreaSource,
  drawnCircles,
  polygonCount,
} from './create-area-source';
import { useAreaPreview } from './use-area-preview';
import { UploadSection } from './upload-section';
import { useCreateArea } from './use-create-area';
import { useIntersectedWells } from './use-intersected-wells';
import {
  SurveySection,
  emptySurveyRow,
  useSurveyAreaPick,
  useSurveyRowsFromUnit,
  type SurveyRow,
} from 'components/survey-description';
import { PolygonsSection } from 'components/query-map/polygons-section';
import { useAtom } from 'jotai';
import { workspaceDrawShapeAtom } from './atoms';
import { DrawnShapeSection } from './drawn-shape-section';
import {
  BoundaryStyleSection,
  type BufferStepper,
} from './boundary-style-section';
import {
  AREA_TYPES,
  CATEGORY_BY_AREA_TYPE,
  CATALOG_BOUNDARY_STYLES,
  CREATION_METHODS,
  DEFAULT_DISPLAY_COLOR,
  drawingHint,
  EMPTY_DETAILS,
  type AreaDetails,
  type AreaType,
  type BoundaryStyle,
  type CreationMethod,
  type SelectedArea,
  type SelectionSource,
} from './create-area-types';
import { EMPTY_PICKED_AREAS } from './utils';
import { SelectionSummary } from './selection-summary';
import { SurveyPickNotices } from './survey-pick-notices';
import { IntersectedWellsSection } from './intersected-wells-section';
import styles from './create-area-panel.module.scss';

/** Shared, so leaving the survey method doesn't hand the map a new array each time. */
const EMPTY_PICKED_IDS: number[] = [];

/** The catalog's selection when none is passed, kept stable for the preview. */
const EMPTY_SELECTION: SelectedArea[] = [];

/**
 * Acreage past which a Unit is worth a second look, per the mock's own copy
 * ("large for a Unit Area (>10,000 ac)").
 */
const UNIT_WARNING_ACRES = 10_000;

/** The panel's subtitle, the same wherever it opens. */
const DEFAULT_SUBTITLE =
  'Choose how the next spatial area should be created. Every method writes to the same geometry, source, status, and lineage context shown in this catalog.';

/** Point rows can only be wrapped, so they start with the mock's buffer. */
const POINT_BUFFER_FT = '660';

const POINT_BOUNDARY_STYLES: readonly BoundaryStyle[] = ['bounding'];

/** The mock's buffer for points: steps of 110 ft. */
const POINT_BUFFER_STEPPER: BufferStepper = {
  step: 110,
  helperText:
    'Distance added around each selected location before the boundary is drawn.',
};

/** Where to check rows on the catalog, said while none are. */
const CATALOG_EMPTY_HINT =
  'Check rows in the areas table to build an area from them.';

interface BoundaryDefaults {
  style: BoundaryStyle;
  buffer: string;
}

/**
 * The style and buffer a selection opens on: the catalog's exact combine with no
 * buffer, or for points the bounding hull at the mock's 660 ft.
 *
 * @param {SelectionSource["geometry"] | undefined} geometry - What the host's rows are, if any.
 * @return {BoundaryDefaults} The starting values.
 */
const boundaryDefaults = (
  geometry: SelectionSource['geometry'] | undefined
): BoundaryDefaults =>
  geometry === 'point'
    ? { style: 'bounding', buffer: POINT_BUFFER_FT }
    : { style: 'exact', buffer: '' };

interface CreateAreaPanelProps {
  open: boolean;
  onClose: () => void;
  /**
   * Reports the area that was just created, so the catalog can refresh and offer
   * a way to open it. The panel deliberately doesn't navigate: the mock keeps the
   * user on the list they were working in.
   */
  onCreated: (area: {
    id: number;
    name: string;
    businessId: string;
    shared: boolean;
    /** The whole create response, for a host that lists the new area. */
    record: Record<string, unknown>;
  }) => void;
  /** Rows checked in the catalog — the sources the from-selected method combines. */
  selectedAreas?: SelectedArea[];
  /** Shared with the host's map, which is where the drawing actually happens. */
  drawing: MapWorkspaceState;
  /**
   * Rows from another host's dataset (Map Query). Takes the place of
   * `selectedAreas`, and decides the boundary styles by their geometry.
   */
  selection?: SelectionSource;
  /** The upload method's boundary files, sent and checked by the host's hook. */
  upload: GeometryUpload;
}

/**
 * Create New Area panel, the same flow in the Areas catalog and Map Query. Area
 * type comes first and gates everything else: it decides which creation methods
 * make sense and which details apply, so no method or detail can be set until it
 * is chosen. Epic 2 adds the circle draw mode.
 *
 * @param {CreateAreaPanelProps} props - The component props.
 * @return {JSX.Element} The side panel.
 */
export const CreateAreaPanel: FC<CreateAreaPanelProps> = ({
  open,
  onClose,
  onCreated,
  selectedAreas: catalogSelection = EMPTY_SELECTION,
  drawing,
  selection,
  upload,
}) => {
  const selectedAreas = selection?.rows ?? catalogSelection;
  const selectionGeometry = selection?.geometry;
  const [areaType, setAreaType] = useState<AreaType | null>(null);
  const [method, setMethod] = useState<CreationMethod | null>(null);
  const [boundaryStyle, setBoundaryStyle] = useState<BoundaryStyle>(
    () => boundaryDefaults(selection?.geometry).style
  );
  // Empty rather than a literal 0, so typing into it replaces nothing; the
  // payload reads a blank buffer as 0.
  const [bufferFt, setBufferFt] = useState(
    () => boundaryDefaults(selection?.geometry).buffer
  );
  const [surveyRows, setSurveyRows] = useState<SurveyRow[]>([emptySurveyRow()]);
  const [details, setDetails] = useState<AreaDetails>(EMPTY_DETAILS);
  // Sharing publishes the area to every Insights map, so Create pauses on a
  // confirmation first (INS-11638). Cancelling returns to the still-open panel
  // with everything as it was.
  const [confirmSharedOpen, setConfirmSharedOpen] = useState(false);
  const { saving, errors, create, reset: resetCreate } = useCreateArea();
  // Epic 2: the drawn method's next shape, shared with the map that draws it.
  const [drawShape, setDrawShape] = useAtom(workspaceDrawShapeAtom);
  const { set: setUploadFiles } = upload;

  // Each open starts clean rather than showing the last attempt's choices.
  useEffect(() => {
    if (open) {
      setAreaType(null);
      setMethod(null);
      setSurveyRows([emptySurveyRow()]);
      setDetails(EMPTY_DETAILS);
      setConfirmSharedOpen(false);
      setDrawShape('polygon');
      setUploadFiles([]);
      resetCreate();
    }
  }, [open, resetCreate, setDrawShape, setUploadFiles]);

  // Each open, and each switch between polygon and point rows (another results
  // tab in Map Query), lands on that geometry's style and buffer: points offer
  // only the bounding hull. Adjusted while rendering, as React advises for state
  // that follows a prop, rather than in an effect.
  const boundaryKey = open ? (selectionGeometry ?? 'catalog') : null;
  const [seenBoundaryKey, setSeenBoundaryKey] = useState(boundaryKey);
  if (boundaryKey !== seenBoundaryKey) {
    setSeenBoundaryKey(boundaryKey);
    if (boundaryKey !== null) {
      const defaults = boundaryDefaults(selectionGeometry);
      setBoundaryStyle(defaults.style);
      setBufferFt(defaults.buffer);
    }
  }

  // Area type gates the method group: picking one unlocks the radios and lands
  // on that type's default, and clearing it puts the panel back to unchosen.
  // Units open on Area from Survey, the method their notice recommends.
  useEffect(() => {
    if (areaType === null) {
      setMethod(null);
    } else {
      setMethod(areaType === 'Units' ? 'survey' : 'selected');
    }
  }, [areaType]);

  const isUnit = areaType === 'Units';
  const isTract = areaType === 'Tracts';
  const methodsDisabled = areaType === null;

  // Picking the drawn method arms the map straight away, or it looks inert with the
  // Draw button below the fold. Re-arms after a clear AND after an aborted draw —
  // a double-click before three corners exist makes MapboxDraw discard the shape
  // and leave draw mode, which otherwise left the map dead until the panel's
  // button was found. Disarms on leaving the method.
  //
  // Only while the panel is open: closing it abandons the drawing, and the method
  // it was on outlives the close (the form resets on the next open). Clearing the
  // shapes then read as "nothing drawn yet" and re-armed the map the user had just
  // handed back, leaving it in draw mode with no panel in sight (UAT).
  const noPolygonsYet = drawing.polygons.length === 0;
  const isDrawingNow = drawing.isDrawing;
  const { onIsDrawingChange, onNeedsMapChange } = drawing;
  useEffect(() => {
    if (open && method === 'drawn' && noPolygonsYet && !isDrawingNow) {
      onIsDrawingChange(true);
    } else if (!open || method !== 'drawn') {
      onIsDrawingChange(false);
    }
  }, [open, method, noPolygonsYet, isDrawingNow, onIsDrawingChange]);

  // Drawing a boundary and picking survey sections both happen on the map, so
  // those two methods grow it; the others — and a closed panel — hand it back to
  // its usual size.
  useEffect(() => {
    onNeedsMapChange(open && (method === 'drawn' || method === 'survey'));
  }, [open, method, onNeedsMapChange]);

  // Changing method discards what the previous one had: only one method's inputs are
  // ever submitted, and switching back and forth would otherwise accumulate shapes.
  const { onPolygonsChange, onEditingIndexChange } = drawing;
  useEffect(() => {
    if (method !== 'drawn') {
      onPolygonsChange([]);
      onEditingIndexChange(null);
    }
    if (method !== 'survey') {
      setSurveyRows([emptySurveyRow()]);
    }
    if (method !== 'upload') {
      setUploadFiles([]);
    }
  }, [method, onPolygonsChange, onEditingIndexChange, setUploadFiles]);

  const patchDetails = (patch: Partial<AreaDetails>) =>
    setDetails((current) => ({ ...current, ...patch }));

  const drawnPolygons = drawing.polygons;

  // The map highlights the sections already in the rows. Keyed on the ids rather than
  // the rebuilt array, so it doesn't re-notify the map on every keystroke.
  const surveyLocationKey = surveyRows
    .map((row) => row.location?.id)
    .filter((id): id is number => id !== undefined)
    .join(',');
  const surveyLocationIds = useMemo(() => {
    return surveyLocationKey === ''
      ? EMPTY_PICKED_IDS
      : surveyLocationKey.split(',').map(Number);
  }, [surveyLocationKey]);

  const hasGeometry =
    (method === 'selected' && selectedAreas.length > 0) ||
    (method === 'drawn' && drawnPolygons.length > 0) ||
    (method === 'survey' && surveyLocationIds.length > 0) ||
    (method === 'upload' && upload.result !== null);

  // Points can only be wrapped; polygons offer every style, in either host.
  const offeredStyles =
    selectionGeometry === 'point'
      ? POINT_BOUNDARY_STYLES
      : CATALOG_BOUNDARY_STYLES;
  // What the selected rows are called, so a count of wells reads as wells.
  const selectionNoun = selection?.noun ?? 'Areas';
  const selectionSingular = selection?.singular ?? 'Area';

  /**
   * The wire `source` for a chosen method, from the current inputs.
   *
   * @param {CreationMethod} chosen - The creation method.
   * @return {object} The kind-tagged source.
   */
  const sourceFor = (chosen: CreationMethod) =>
    buildAreaSource({
      method: chosen,
      surveyRows,
      drawnPolygons,
      selectedAreas,
      boundaryStyle,
      bufferFt,
      selectionKind: selection?.kind,
      upload: upload.result && {
        result: upload.result,
        fileSignedIds: upload.fileSignedIds,
      },
    });

  // Preview the shape the current inputs describe, once there is geometry to derive
  // one from. The routing fields ride along because a Unit's dry run validates the
  // development-area record too.
  // Only a Unit's dry run validates the name — its routed development area owns
  // the unique one — so only a Unit re-derives as the name is typed. For every
  // other type the name changes nothing the preview reports.
  const previewName = isUnit ? details.name.trim() || undefined : undefined;
  const { horizontalUnit } = details;
  const { result: uploadResult, fileSignedIds } = upload;
  // Built only when an input it reads changes: a file's whole shape rides in the
  // source, and serializing that on every keystroke elsewhere in the form lags.
  const previewInput = useMemo(
    () =>
      open && hasGeometry && areaType !== null && method !== null
        ? {
            source: buildAreaSource({
              method,
              surveyRows,
              drawnPolygons,
              selectedAreas,
              boundaryStyle,
              bufferFt,
              selectionKind: selection?.kind,
              upload: uploadResult && { result: uploadResult, fileSignedIds },
            }),
            name: previewName,
            category: CATEGORY_BY_AREA_TYPE[areaType],
            metadata: buildAreaMetadata(isUnit, {
              ...EMPTY_DETAILS,
              horizontalUnit,
            }),
          }
        : null,
    [
      open,
      hasGeometry,
      areaType,
      method,
      surveyRows,
      drawnPolygons,
      selectedAreas,
      boundaryStyle,
      bufferFt,
      selection?.kind,
      uploadResult,
      fileSignedIds,
      previewName,
      isUnit,
      horizontalUnit,
    ]
  );
  const preview = useAreaPreview(previewInput);

  // Nothing that previews clean is refused at save, so a refusal here is the
  // same refusal the Create button would earn — better shown now than after it.
  const canCreate =
    areaType !== null &&
    details.name.trim() !== '' &&
    hasGeometry &&
    preview.errors.length === 0 &&
    !saving;

  // Shapes worth a second look before saving: a Unit is normally one polygon of a
  // few thousand acres, so more parts or far more acreage is usually a mistake —
  // warned about rather than blocked, since both are legal.
  const shapeAcres =
    preview.grossAcres ??
    (method === 'drawn' && drawnPolygons.length > 0
      ? drawnPolygons.reduce(
          (total, polygon) => total + polygonAreaAcres(polygon),
          0
        )
      : null);
  // Counted from the preview, which merges overlapping drawn shapes and circles,
  // so two touching shapes read as the one part they save as.
  const shapeParts = polygonCount(preview.geometry);

  const warnings =
    !isUnit || !hasGeometry
      ? []
      : [
          ...(shapeAcres !== null && shapeAcres > UNIT_WARNING_ACRES
            ? [
                {
                  id: 'large-unit-area',
                  title: 'Large Unit Area',
                  subtitle:
                    'The geometry of the proposed Area is large for a Unit Area (>10,000 ac); please verify the selected geometry before submitting.',
                },
              ]
            : []),
          ...(shapeParts > 1
            ? [
                {
                  id: 'multi-polygon-unit-area',
                  title: 'Multiple polygons',
                  subtitle:
                    'The geometry of the proposed Area contains multiple polygons, this is unusual for a Unit Area; please verify the selected geometry before submitting.',
                },
              ]
            : []),
        ];

  // Clicking a unit on the map fills the survey rows from its own tracts.
  const surveyRowsFromUnit = useSurveyRowsFromUnit(setSurveyRows);

  // Clicking a section of the survey grid picks that one tract, and clicking a
  // picked one drops it again.
  const surveyAreaPick = useSurveyAreaPick(surveyRows, setSurveyRows);

  // Both picks are only offered while the survey rows are on screen to receive
  // them — so while the panel is open, on the method that has those rows; the rest
  // of the time the map's unit layers stay plain scenery and the survey grid isn't
  // drawn at all.
  const { onUnitClickChange, onSurveyAreaPickChange } = drawing;
  useEffect(() => {
    onUnitClickChange(
      open && method === 'survey' ? surveyRowsFromUnit.load : null
    );
    return () => onUnitClickChange(null);
  }, [open, method, surveyRowsFromUnit.load, onUnitClickChange]);

  useEffect(() => {
    onSurveyAreaPickChange(
      open && method === 'survey' ? surveyAreaPick.pick : null
    );
    return () => onSurveyAreaPickChange(null);
  }, [open, method, surveyAreaPick.pick, onSurveyAreaPickChange]);

  // What the map draws as picked: each row's area carved by its quarter calls,
  // so a `W2` row highlights the west half rather than the whole section. Keyed
  // on ids and calls rather than the rebuilt array, so typing elsewhere in the
  // form doesn't re-notify the map.
  const pickedSurveyAreasKey = surveyRows
    .map((row) => {
      if (!row.location) {
        return '';
      }
      return `${row.location.id}:${row.quarterCalls.join('+')}`;
    })
    .filter(Boolean)
    .join(',');
  const pickedSurveyAreas = useMemo(() => {
    if (pickedSurveyAreasKey === '') {
      return EMPTY_PICKED_AREAS;
    }
    return surveyRows
      .filter(
        (row): row is SurveyRow & { location: ReferenceLocationResponse } =>
          row.location !== undefined
      )
      .map((row) => ({
        location: row.location,
        quarterCalls: row.quarterCalls,
      }));
    // The key covers everything read from the rows, so the array only rebuilds
    // when a pick or a quarter call actually changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedSurveyAreasKey]);

  const { onPickedSurveyAreasChange } = drawing;
  useEffect(() => {
    onPickedSurveyAreasChange(
      method === 'survey' ? pickedSurveyAreas : EMPTY_PICKED_AREAS
    );
  }, [method, pickedSurveyAreas, onPickedSurveyAreasChange]);

  // Units match wells against the previewed boundary. The results are dropped
  // whenever that shape changes, so a count never outlives what it measured.
  const wells = useIntersectedWells(isUnit ? preview.geometry : null);

  // Hand the previewed shape to the map, so the composite a from-selected or
  // survey build would produce is visible before saving.
  const { onPreviewGeometryChange } = drawing;
  useEffect(() => {
    onPreviewGeometryChange(method === 'drawn' ? null : preview.geometry);
  }, [method, preview.geometry, onPreviewGeometryChange]);

  // Once the display colour is changed from its default, the shapes on the map
  // are refilled to match, so what is drawn looks like the area it becomes. At
  // the default the map keeps its usual preview blue — drawing starts blue, and
  // only an actual colour choice repaints it.
  const { onPreviewColorChange } = drawing;
  useEffect(() => {
    onPreviewColorChange(
      open && details.displayColor !== DEFAULT_DISPLAY_COLOR
        ? details.displayColor
        : null
    );
  }, [open, details.displayColor, onPreviewColorChange]);

  /**
   * Posts the area. The geometry rides in `source`; the rest is common.
   */
  const handleCreate = async () => {
    if (!canCreate || areaType === null || method === null) return;

    const payload: Record<string, unknown> = {
      name: details.name.trim(),
      category: CATEGORY_BY_AREA_TYPE[areaType],
      notes: details.notes || undefined,
      // One kind-tagged `source` carries the geometry inputs; `source.type` is
      // the only place the creation method is named, and the backend derives
      // `source_method` from it.
      source: sourceFor(method),
    };

    if (!isUnit) {
      payload.display_color = details.displayColor;
      payload.purpose = details.purpose || undefined;
      payload.shared = details.shared;
      // The Verified control's verdict, sent only when one is on. Correct blocks
      // automatic GIS updates to the new area from the start; incorrect records the
      // verdict without blocking them.
      if (details.verification === 'correct') {
        payload.mark_correct = true;
      } else if (details.verification === 'incorrect') {
        payload.mark_incorrect = true;
      }
    }

    // Drawn circles travel as polygons in the source; their centre and radius
    // ride along so the area can hand them back as circles.
    const circles = method === 'drawn' ? drawnCircles(drawnPolygons) : [];
    const unitMetadata = buildAreaMetadata(isUnit, details);
    const metadata =
      circles.length > 0 ? { ...unitMetadata, circles } : unitMetadata;
    if (metadata) {
      payload.metadata = metadata;
    }

    const created = await create(payload);
    if (!created) {
      return;
    }

    onClose();
    // Stay on the list, as the mock does — the catalog refreshes and offers a link
    // to the new area, which rarely lands on the page being looked at.
    onCreated({
      id: created.id,
      name: details.name.trim(),
      businessId: created.businessId,
      // Units never send the flag, whatever an earlier type left in the form.
      shared: !isUnit && details.shared,
      record: created,
    });
  };

  /**
   * The Create action. A shared area lands on every user's Insights maps, so
   * that one case stops on a confirmation before posting (INS-11638). Units
   * never collect the Shared flag, so they create directly.
   */
  const handleCreateRequest = () => {
    if (!isUnit && details.shared) {
      setConfirmSharedOpen(true);
    } else {
      void handleCreate();
    }
  };

  // Area type comes first: it gates everything else.
  const areaTypeField = (
    <Dropdown
      id="create-area-type"
      titleText="Area type"
      label="Select area type"
      items={[...AREA_TYPES]}
      selectedItem={areaType}
      onChange={({ selectedItem }: { selectedItem: AreaType | null }) =>
        setAreaType(selectedItem)
      }
    />
  );

  const typeNotices = (
    <>
      {isUnit && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Recommended creation method"
          subtitle="For Unit Areas, creation methods that produce clean, non-overlapping boundaries are preferred. We recommend using Area from Survey."
        />
      )}

      {isTract && (
        <InlineNotification
          kind="info"
          lowContrast
          hideCloseButton
          title="Geometry only"
          subtitle="Creating this area will generate the tract geometry only. Visit the CRM Tract page to generate workflow-ready CRM Tracts."
        />
      )}
    </>
  );

  const methodRadios = CREATION_METHODS.map(({ id, title, description }) => (
    <RadioButton
      key={id}
      id={`create-area-method-${id}`}
      value={id}
      disabled={methodsDisabled}
      labelText={
        <span className={styles.optionLabel}>
          <span>{title}</span>
          <span className={styles.optionDescription}>{description}</span>
        </span>
      }
    />
  ));

  return (
    <>
      {/*
        The house danger-confirmation modal, as the mock styles it — portaled,
        so the dialog isn't clipped or stacked under the panel it confirms for.
      */}
      <DestroyConfirmation
        open={confirmSharedOpen}
        size="md"
        className={styles.sharedConfirm}
        modalHeading="Create a shared area?"
        primaryButtonText="Create shared area"
        secondaryButtonText="Cancel"
        onRequestClose={() => setConfirmSharedOpen(false)}
        onRequestSubmit={() => {
          setConfirmSharedOpen(false);
          void handleCreate();
        }}
      >
        Are you sure you would like to create a shared area? This will add this
        area to all Insights maps as a layer.
      </DestroyConfirmation>
      <SidePanel
        open={open}
        onRequestClose={onClose}
        title="Create New Area"
        subtitle={DEFAULT_SUBTITLE}
        slideIn
        selectorPageContent="#main-content"
        placement="right"
        size="md"
        actions={[
          { kind: 'secondary' as const, label: 'Cancel', onClick: onClose },
          {
            kind: 'primary' as const,
            label: saving ? 'Creating…' : 'Create area',
            disabled: !canCreate,
            onClick: handleCreateRequest,
          },
        ]}
      >
        <div className={styles.body}>
          {errors.length > 0 && (
            <InlineNotification
              kind="error"
              lowContrast
              title="Could not create the area"
              subtitle={errors.join(' ')}
              onCloseButtonClick={resetCreate}
            />
          )}

          {selectedAreas.length > 0 && (
            <SelectionSummary
              rows={selectedAreas}
              singular={selectionSingular}
              noun={selectionNoun}
            />
          )}

          {areaTypeField}

          {typeNotices}

          <section className={styles.section}>
            <h4 className={styles.sectionTitle}>Creation method</h4>
            {methodsDisabled && (
              <p className={styles.hint}>
                Select an area type to choose a creation method and fill in the
                area details.
              </p>
            )}

            <RadioButtonGroup
              name="create-area-method"
              // The visible heading sits outside the fieldset, so the group needs
              // its own name — `legendText` would render a second one.
              legendText=""
              aria-label="Creation method"
              orientation="vertical"
              // Before a type unlocks the group the mock still shows the first
              // method marked, so the disabled state reads as a default.
              valueSelected={method ?? CREATION_METHODS[0].id}
              onChange={(value: string | number | undefined) =>
                setMethod(
                  value === undefined ? null : (String(value) as CreationMethod)
                )
              }
            >
              {methodRadios}
            </RadioButtonGroup>
          </section>

          {/* The shape to draw sits under the methods, as the mock has it. */}
          {method === 'drawn' && (
            <DrawnShapeSection
              shape={drawShape}
              onShapeChange={setDrawShape}
              // Swapping while armed swaps the armed tool; an edit holds it.
              disabled={drawing.editingIndex !== null}
            />
          )}

          {/*
            The selection names itself, as the Map Query mock has it, in both
            hosts: the count when there are rows, a pointer to the table when
            there are none. It sits flush on the boundary styles, as one block.
          */}
          {method === 'selected' && (
            <div className={styles.selectedBlock}>
              {selectedAreas.length > 0 ? (
                <p className={styles.methodHint}>
                  {formatNumber(selectedAreas.length, 0, 0)}{' '}
                  {selectedAreas.length === 1
                    ? selectionSingular
                    : selectionNoun}{' '}
                  selected. The area draws on the map as you adjust it.
                </p>
              ) : (
                <InlineNotification
                  kind="info"
                  lowContrast
                  hideCloseButton
                  title="Nothing selected"
                  subtitle={selection?.emptyHint ?? CATALOG_EMPTY_HINT}
                />
              )}
              <BoundaryStyleSection
                style={boundaryStyle}
                bufferFt={bufferFt}
                onStyleChange={setBoundaryStyle}
                onBufferChange={setBufferFt}
                offered={offeredStyles}
                boundingDescription={`Generate a single boundary that encloses all selected ${selectionNoun.toLowerCase()} (convex hull of the selection).`}
                bufferStepper={
                  selectionGeometry === 'point'
                    ? POINT_BUFFER_STEPPER
                    : undefined
                }
              />
            </div>
          )}

          {method === 'survey' && (
            <>
              <SurveyPickNotices
                surveyRowsFromUnit={surveyRowsFromUnit}
                surveyAreaPick={surveyAreaPick}
              />
              <SurveySection rows={surveyRows} onChange={setSurveyRows} />
            </>
          )}

          {method === 'upload' && (
            <UploadSection
              upload={upload}
              shownHint="Shown on the map. Review it before you create the area."
            />
          )}

          {/*
          Shapes worth checking before saving, not reasons to stop: a Unit can
          legitimately be huge or multi-part, so these warn where the dry run's
          errors block.
        */}
          {warnings.map((warning) => (
            <InlineNotification
              key={warning.id}
              kind="warning"
              lowContrast
              hideCloseButton
              title={warning.title}
              subtitle={warning.subtitle}
            />
          ))}

          {/*
          What the dry run refused, while there is still a panel to fix it in.
          For a Unit these are the development-area rules — five sections at
          most, contiguous, no section twice, and a name not already taken.
        */}
          {preview.errors.length > 0 && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title="This area cannot be created yet"
              subtitle={preview.errors.join(' ')}
            />
          )}

          {/*
          Acreage as the backend derives it, not measured here — the preview runs
          the same builders the save will, so this is the figure that gets stored.

          A preview can answer without one: the drawn branch of
          `AreaGeometries::SourceGeometry` returns the geometry alone, where the
          survey and combine builders both return `gross_acres`. So "calculating"
          is shown only while a call is actually out — otherwise a drawn boundary
          claims to be calculating a figure that is never coming. Each drawn
          polygon still carries its own measured acreage in the list above.
        */}
          {hasGeometry &&
            preview.errors.length === 0 &&
            (preview.loading || preview.grossAcres !== null) && (
              <p className={styles.previewAcres} aria-live="polite">
                {preview.grossAcres === null
                  ? 'Calculating acreage…'
                  : // Two decimals, matching the catalog's Gross Acres column.
                    `${formatNumber(preview.grossAcres, 2, 2)} gross acres`}
              </p>
            )}

          {method !== null && (
            <CreateAreaDetails
              details={details}
              onChange={patchDetails}
              isUnit={isUnit}
            />
          )}

          {method === 'drawn' && (
            <PolygonsSection
              shape={drawShape}
              // Under the Polygons heading while armed, as the mock has it.
              drawingHint={drawingHint(drawShape, drawnPolygons.length)}
              emptyHint="No polygons drawn yet."
              polygons={drawnPolygons}
              isDrawing={drawing.isDrawing}
              onDraw={() => drawing.onIsDrawingChange(true)}
              onDelete={(index) => {
                // Editing state follows the delete: deleting the edited polygon
                // ends the edit, deleting one before it keeps the edit on the
                // same shape at its new index.
                if (drawing.editingIndex !== null) {
                  if (drawing.editingIndex === index) {
                    drawing.onEditingIndexChange(null);
                  } else if (drawing.editingIndex > index) {
                    drawing.onEditingIndexChange(drawing.editingIndex - 1);
                  }
                }
                drawing.onPolygonsChange(
                  drawnPolygons.filter((_, i) => i !== index)
                );
              }}
              editingIndex={drawing.editingIndex}
              onEditingIndexChange={drawing.onEditingIndexChange}
              snapToGrid={drawing.snapToGrid}
              onSnapToGridChange={drawing.onSnapToGridChange}
            />
          )}

          {isUnit && method !== null && (
            <IntersectedWellsSection
              wells={wells}
              hasShape={!!preview.geometry}
            />
          )}
        </div>
      </SidePanel>
    </>
  );
};

export default CreateAreaPanel;
